import React, { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, Divider, IconButton,
  InputAdornment, MenuItem, TextField, Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit, Search } from "@mui/icons-material";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import useAuth from "../hoc/AuthProvider/useAuth";
import { Permissions } from "../config/permissions";
import { getCompanyList, createCompany, updateCompany, deleteCompany } from "../services/companyService";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CompanyDto {
  id: number;
  parentId: number | null;
  parentName: string | null;
  name: string;
  email: string;
  phone: string;
  address: string;
  isActive: boolean;
}

const emptyForm = { parentId: null as number | null, name: "", email: "", phone: "", address: "" };

const Companies: React.FC = () => {
  const { havePermission } = useAuth();
  const { showSuccess, showError, snackbarProps } = useSnackbar();

  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof emptyForm, string>>>({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCompanyList();
      setCompanies((res.data as any).data ?? []);
    } catch (e) {
      showError(e as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const validate = () => {
    const errs: typeof errors = {};
    if (!form.name.trim()) errs.name = "Şirket adı boş olamaz.";
    else if (form.name.trim().length > 200) errs.name = "Şirket adı en fazla 200 karakter olabilir.";
    if (form.email && !EMAIL_REGEX.test(form.email)) errs.email = "Geçersiz e-posta formatı.";
    return errs;
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (c: CompanyDto) => {
    setEditId(c.id);
    setForm({ parentId: c.parentId ?? null, name: c.name, email: c.email ?? "", phone: c.phone ?? "", address: c.address ?? "" });
    setErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setErrors({}); };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    try {
      if (editId !== null) {
        await updateCompany({ id: editId, ...form });
        showSuccess("Şirket güncellendi.");
      } else {
        await createCompany(form);
        showSuccess("Şirket oluşturuldu.");
      }
      setDialogOpen(false);
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await deleteCompany(id);
      if (!(res.data as any).success) { showError(new Error((res.data as any).message)); return; }
      showSuccess("Şirket silindi.");
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Şirketler</Typography>
        {havePermission(Permissions.Company_Create) && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
            Yeni Şirket
          </Button>
        )}
      </Box>

      <TextField size="small" placeholder="Ara..." value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        sx={{ mb: 2, width: 280 }} />

      <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        {loading ? (
          <Typography sx={{ p: 3, color: "text.secondary" }}>Yükleniyor...</Typography>
        ) : filtered.length === 0 ? (
          <Typography sx={{ p: 3, color: "text.secondary" }}>Kayıt bulunamadı.</Typography>
        ) : filtered.map((c, i) => (
          <Box key={c.id}>
            {i > 0 && <Divider />}
            <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600} fontSize={14}>
                  {c.parentName ? `${c.parentName} › ${c.name}` : c.name}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {[c.email, c.phone, c.address].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              {havePermission(Permissions.Company_Update) && (
                <IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
              )}
              {havePermission(Permissions.Company_Delete) && (
                <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editId ? "Şirketi Düzenle" : "Yeni Şirket"}
          <IconButton onClick={handleClose}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField select size="small" label="Üst Şirket" value={form.parentId ?? ""}
            onChange={(e) => setForm({ ...form, parentId: e.target.value === "" ? null : Number(e.target.value) })}
            fullWidth>
            <MenuItem value=""><em>— Yok (Kök Şirket) —</em></MenuItem>
            {companies.filter((c) => c.id !== editId).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.parentName ? `${c.parentName} › ${c.name}` : c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Şirket Adı *" value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors((p) => ({ ...p, name: undefined })); }}
            error={!!errors.name} helperText={errors.name} fullWidth />
          <TextField size="small" label="E-posta" value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors((p) => ({ ...p, email: undefined })); }}
            error={!!errors.email} helperText={errors.email} fullWidth />
          <TextField size="small" label="Telefon" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth />
          <TextField size="small" label="Adres" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} fullWidth multiline rows={2} />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>İptal</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
            Kaydet
          </Button>
        </Box>
      </Dialog>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Companies;
